import React, { useEffect, useState } from 'react';

import { Botao, Checkbox, Field, Input, Modal, Select } from '../../../ui';
import { FiltrosLogGeral, OpcoesLogGeral, TipoDeAtor } from '../../../api/log-geral/logGeralApi';
import { ROTULO_TIPO_DE_ATOR } from '../../../utils/logGeralRegras';

/**
 * Filtros da tela Logs do Sistema (at-85). Trabalha sobre RASCUNHO e só devolve no "Aplicar"
 * (padrão at-28/at-65): mexer num campo não dispara busca.
 *
 * As datas ficam no formato do `<input type="date">` (`AAAA-MM-DD`); quem converte para o
 * intervalo do dia é `paraFiltrosDaApi`.
 */
export interface FiltrosLogs {
  de: string;
  ate: string;
  pessoa: string;
  atorTipo: TipoDeAtor | '';
  modulo: string;
  acao: string;
  entidade: string;
  entidadeId: string;
  soFalhas: boolean;
  incluirNegocio: boolean;
}

export const FILTROS_LOGS_VAZIOS: FiltrosLogs = {
  de: '',
  ate: '',
  pessoa: '',
  atorTipo: '',
  modulo: '',
  acao: '',
  entidade: '',
  entidadeId: '',
  soFalhas: false,
  incluirNegocio: false,
};

export function contarFiltrosLogsAtivos(f: FiltrosLogs): number {
  return (Object.keys(FILTROS_LOGS_VAZIOS) as (keyof FiltrosLogs)[]).filter(
    (k) => f[k] !== FILTROS_LOGS_VAZIOS[k] && String(f[k]).trim() !== '',
  ).length;
}

/**
 * Para a API. ⚠️ "Até" é o FIM do dia escolhido, no fuso do navegador — sem isso, escolher
 * "até hoje" excluiria tudo o que aconteceu hoje.
 */
export function paraFiltrosDaApi(f: FiltrosLogs, q?: string): FiltrosLogGeral {
  const inicioDoDia = (d: string) => new Date(`${d}T00:00:00`).toISOString();
  const fimDoDia = (d: string) => new Date(`${d}T23:59:59.999`).toISOString();
  return {
    de: f.de ? inicioDoDia(f.de) : undefined,
    ate: f.ate ? fimDoDia(f.ate) : undefined,
    pessoa: f.pessoa.trim() || undefined,
    atorTipo: f.atorTipo || undefined,
    modulo: f.modulo || undefined,
    acao: f.acao.trim() || undefined,
    entidade: f.entidade.trim() || undefined,
    entidadeId: f.entidadeId.trim() || undefined,
    soFalhas: f.soFalhas || undefined,
    incluirNegocio: f.incluirNegocio || undefined,
    q: q?.trim() || undefined,
  };
}

export interface ModalFiltrosLogsProps {
  aberto: boolean;
  valor: FiltrosLogs;
  opcoes: OpcoesLogGeral | null;
  onAplicar: (filtros: FiltrosLogs) => void;
  onFechar: () => void;
}

const ModalFiltrosLogs: React.FC<ModalFiltrosLogsProps> = ({ aberto, valor, opcoes, onAplicar, onFechar }) => {
  const [rascunho, setRascunho] = useState<FiltrosLogs>(valor);

  useEffect(() => {
    if (aberto) setRascunho(valor);
  }, [aberto, valor]);

  const mudar = <K extends keyof FiltrosLogs>(campo: K, v: FiltrosLogs[K]) =>
    setRascunho((r) => ({ ...r, [campo]: v }));

  const ativos = contarFiltrosLogsAtivos(rascunho);
  const periodoInvertido = !!rascunho.de && !!rascunho.ate && rascunho.de > rascunho.ate;

  const tipos = opcoes?.tiposDeAtor ?? (Object.keys(ROTULO_TIPO_DE_ATOR) as TipoDeAtor[]).map((v) => ({ valor: v, rotulo: ROTULO_TIPO_DE_ATOR[v] }));

  return (
    <Modal
      aberto={aberto}
      titulo="Filtros"
      descricao="Sem período escolhido, a lista mostra os últimos 7 dias."
      tamanho="md"
      aoFechar={onFechar}
      alinharRodape="direita"
      rodape={
        <>
          <Botao variante="fantasma" disabled={ativos === 0} onClick={() => setRascunho(FILTROS_LOGS_VAZIOS)}>
            Limpar
          </Botao>
          <Botao variante="secundario" onClick={onFechar}>
            Cancelar
          </Botao>
          <Botao variante="primario" disabled={periodoInvertido} onClick={() => onAplicar(rascunho)}>
            Aplicar
          </Botao>
        </>
      }
    >
      <div className="tw-flex tw-flex-col tw-gap-4">
        <div className="tw-grid tw-grid-cols-1 tw-gap-4 sm:tw-grid-cols-2">
          <Field rotulo="De" campo="de">
            <Input type="date" value={rascunho.de} onChange={(e) => mudar('de', e.target.value)} />
          </Field>
          <Field
            rotulo="Até"
            campo="ate"
            erro={periodoInvertido ? 'A data final é anterior à inicial.' : undefined}
          >
            <Input type="date" value={rascunho.ate} onChange={(e) => mudar('ate', e.target.value)} />
          </Field>
        </div>

        <div className="tw-grid tw-grid-cols-1 tw-gap-4 sm:tw-grid-cols-2">
          <Field rotulo="Quem" dica="Parte do nome, ou o e-mail usado no login." campo="pessoa">
            <Input value={rascunho.pessoa} maxLength={100} onChange={(e) => mudar('pessoa', e.target.value)} />
          </Field>
          <Field rotulo="Tipo de ator" campo="atorTipo">
            <Select
              opcoes={[{ valor: '', rotulo: 'Todos' }, ...tipos.map((t) => ({ valor: t.valor, rotulo: t.rotulo }))]}
              placeholder=""
              value={rascunho.atorTipo}
              onChange={(e) => mudar('atorTipo', e.target.value as TipoDeAtor | '')}
            />
          </Field>
        </div>

        <div className="tw-grid tw-grid-cols-1 tw-gap-4 sm:tw-grid-cols-2">
          <Field rotulo="Módulo" campo="modulo">
            <Select
              opcoes={[{ valor: '', rotulo: 'Todos' }, ...(opcoes?.modulos ?? []).map((m) => ({ valor: m, rotulo: m }))]}
              placeholder=""
              value={rascunho.modulo}
              onChange={(e) => mudar('modulo', e.target.value)}
            />
          </Field>
          <Field
            rotulo="Ação"
            dica="Código exato, ou o começo terminado em ponto (ex.: envio_sga.)."
            campo="acao"
          >
            <Input
              value={rascunho.acao}
              maxLength={80}
              list="log-geral-acoes"
              onChange={(e) => mudar('acao', e.target.value)}
            />
          </Field>
        </div>
        {/* Sugestões das ações já vistas, sem prender o campo a elas (o prefixo não está na lista). */}
        <datalist id="log-geral-acoes">
          {(opcoes?.acoes ?? []).map((a) => (
            <option key={a} value={a} />
          ))}
        </datalist>

        <div className="tw-grid tw-grid-cols-1 tw-gap-4 sm:tw-grid-cols-2">
          <Field rotulo="Tipo de registro" dica="Ex.: associado, carro, contrato, user." campo="entidade">
            <Input value={rascunho.entidade} maxLength={60} onChange={(e) => mudar('entidade', e.target.value)} />
          </Field>
          <Field
            rotulo="Identificador do registro"
            dica="Id, placa ou CPF (só dígitos). Com ele, o período padrão deixa de valer."
            campo="entidadeId"
          >
            <Input value={rascunho.entidadeId} maxLength={64} onChange={(e) => mudar('entidadeId', e.target.value)} />
          </Field>
        </div>

        <Checkbox
          rotulo="Só o que falhou ou foi recusado"
          checked={rascunho.soFalhas}
          onChange={(marcado) => mudar('soFalhas', marcado)}
        />
        <Checkbox
          rotulo="Mostrar também o que cada ação gravou"
          descricao="Por padrão a lista mostra uma linha por ação; o que ela gravou aparece ao abrir o registro."
          checked={rascunho.incluirNegocio}
          onChange={(marcado) => mudar('incluirNegocio', marcado)}
        />
      </div>
    </Modal>
  );
};

export default ModalFiltrosLogs;
